/******
 * This file was generated by a Rake task.
 * Do not modify this file manually.
 * Add changes to the `Equipment.ts` file.
 ******/
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

import { Equipment } from "./Equipment";

import { Area } from "./Area";
import { AreaHasEquipment } from "./AreaHasEquipment";
import { CalendarEventResource } from "./CalendarEventResource";
import { MapObject } from "./MapObject";
import { Project } from "./Project";

export type attachmentMethod = "file" | "iframe";

export const attachmentMethodValues = { file: 0, iframe: 1 };

export abstract class EquipmentModel {
    id: number;
    archived: boolean;
    attachmentMethod: attachmentMethod;
    bannedVehicles: string[];
    createdAt: Dayjs;
    details: string | null;
    equipmentIframe: string | null;
    equipmentName: string | null;
    equipmentType: string | null;
    isDropOff: boolean;
    matchJobsiteHours: boolean;
    projectId: number | null;
    updatedAt: Dayjs;
    repeatingCalendarEventDefinitions: any | null;
    area: Area | null | undefined;
    areaHasEquipment: AreaHasEquipment[] | undefined;
    calendarEventResources: CalendarEventResource[] | undefined;
    mapObject: MapObject | null | undefined;
    project: Project | null | undefined;
    _destroy: boolean | undefined;

    constructor(
        id: number,
        archived: boolean,
        attachmentMethod: attachmentMethod,
        bannedVehicles: string[],
        createdAt: Dayjs,
        details: string | null,
        equipmentIframe: string | null,
        equipmentName: string | null,
        equipmentType: string | null,
        isDropOff: boolean,
        matchJobsiteHours: boolean,
        projectId: number | null,
        updatedAt: Dayjs,
        repeatingCalendarEventDefinitions: any | null,
        area: Area | null | undefined,
        areaHasEquipment: AreaHasEquipment[] | undefined,
        calendarEventResources: CalendarEventResource[] | undefined,
        mapObject: MapObject | null | undefined,
        project: Project | null | undefined
    ) {
        this.id = id;
        this.archived = archived;
        this.attachmentMethod = attachmentMethod;
        this.bannedVehicles = bannedVehicles;
        this.createdAt = createdAt;
        this.details = details;
        this.equipmentIframe = equipmentIframe;
        this.equipmentName = equipmentName;
        this.equipmentType = equipmentType;
        this.isDropOff = isDropOff;
        this.matchJobsiteHours = matchJobsiteHours;
        this.projectId = projectId;
        this.updatedAt = updatedAt;
        this.repeatingCalendarEventDefinitions = repeatingCalendarEventDefinitions;
        this.area = area;
        this.areaHasEquipment = areaHasEquipment;
        this.calendarEventResources = calendarEventResources;
        this.mapObject = mapObject;
        this.project = project;
    }

    static fromApiObject(json: any, timezone: string): Equipment {
        const newObject = new Equipment(
            json["id"],
            json["archived"],
            json["attachment_method"],
            json["banned_vehicles"],
            dayjs(json["created_at"]).tz(timezone),
            json["details"],
            json["equipment_iframe"],
            json["equipment_name"],
            json["equipment_type"],
            json["is_drop_off"],
            json["match_jobsite_hours"],
            json["project_id"],
            dayjs(json["updated_at"]).tz(timezone),
            json["repeating_calendar_event_definitions"],
            json["area"] == null ? json["area"] : Area.fromApiObject(json["area"], timezone),
            json["area_has_equipment"]?.map((item: any) => AreaHasEquipment.fromApiObject(item, timezone)),
            json["calendar_event_resources"]?.map((item: any) => CalendarEventResource.fromApiObject(item, timezone)),
            json["map_object"] == null ? json["map_object"] : MapObject.fromApiObject(json["map_object"], timezone),
            json["project"] == null ? json["project"] : Project.fromApiObject(json["project"], timezone)
        );

        return newObject;
    }

    toApiObject(): any {
        return {
            id: this.id,
            archived: this.archived,
            attachment_method: this.attachmentMethod,
            banned_vehicles: this.bannedVehicles,
            created_at: this.createdAt,
            details: this.details,
            equipment_iframe: this.equipmentIframe,
            equipment_name: this.equipmentName,
            equipment_type: this.equipmentType,
            is_drop_off: this.isDropOff,
            match_jobsite_hours: this.matchJobsiteHours,
            project_id: this.projectId,
            updated_at: this.updatedAt,
            area_has_equipment: this.areaHasEquipment?.map((item) => item.toApiObject()),
            calendar_event_resources_attributes: this.calendarEventResources?.map((item) => item.toApiObject()),
            map_object_attributes: this.mapObject?.toApiObject(),
            _destroy: this._destroy,
        };
    }

    // NOTE: This could potentially cause infinite loops if the object graph is
    // circular.
    inAlternateTimezone(timezone: string): Equipment {
        const clone = Object.create(this) as Equipment;

        for (const key of Object.keys(this)) {
            if (this[key] instanceof dayjs) {
                // @ts-expect-error: Typescript thinks that this is not a dayjs object even though it is checked on the line before
                clone[key] = this[key].tz(timezone);
            } else if (Array.isArray(this[key])) {
                clone[key] = this[key].map((item) =>
                    item.inAlternateTimezone ? item.inAlternateTimezone(timezone) : item
                );
            } else if (this[key] && this[key].inAlternateTimezone) {
                clone[key] = this[key].inAlternateTimezone(timezone);
            }
        }

        return clone;
    }
}
